import type { sessionResponseSchema } from '@beta-assur/shared';
import type { z } from 'zod';

// Client HTTP du portail : jeton d'accès en mémoire seulement (jamais en localStorage),
// rafraîchissement par le cookie HttpOnly de /v1/auth/refresh, erreurs RFC 9457.

export type Session = z.infer<typeof sessionResponseSchema>;

export const MFA_REQUIRED = 'urn:beta-assur:problem:mfa-required';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
    readonly type = 'about:blank',
    readonly requestId?: string,
  ) {
    super(detail ? `${title} : ${detail}` : title);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Query;
  /** Renvoie la réponse brute (téléchargement d'un fichier). */
  raw?: boolean;
}

export interface ApiClient {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>;
  get<T>(path: string, query?: Query): Promise<T>;
  login(input: {
    tenantCode: string;
    email: string;
    password: string;
    totp?: string;
  }): Promise<Session>;
  refresh(): Promise<Session | null>;
  logout(): Promise<void>;
  /** Session courante, lue de façon synchrone (gardes de route). */
  current(): Session | null;
  /** Prévenu quand la session change (connexion, rafraîchissement, perte). */
  onSession(listener: (session: Session | null) => void): () => void;
}

export function buildQuery(query: Query = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

async function toError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as {
      title?: string;
      detail?: string;
      type?: string;
      request_id?: string;
    };
    return new ApiError(
      res.status,
      body.title ?? res.statusText,
      body.detail,
      body.type,
      body.request_id,
    );
  } catch {
    return new ApiError(res.status, res.statusText || 'Erreur');
  }
}

export function createApiClient(
  options: { baseUrl?: string; fetch?: typeof fetch } = {},
): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init));
  let session: Session | null = null;
  let refreshing: Promise<Session | null> | null = null;
  const listeners = new Set<(session: Session | null) => void>();

  const setSession = (next: Session | null) => {
    session = next;
    for (const listener of listeners) listener(next);
  };

  async function send(method: string, path: string, opts: RequestOptions, token: string | null) {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (token) headers.authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (opts.form) body = opts.form;
    else if (opts.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    return doFetch(`${baseUrl}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body }),
    });
  }

  async function refresh(): Promise<Session | null> {
    refreshing ??= (async () => {
      try {
        const res = await send('POST', '/v1/auth/refresh', {}, null);
        const next = res.ok ? ((await res.json()) as Session) : null;
        setSession(next);
        return next;
      } catch {
        setSession(null);
        return null;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    let res = await send(method, path, opts, session?.accessToken ?? null);
    if (res.status === 401 && session && !path.startsWith('/v1/auth/')) {
      const renewed = await refresh();
      if (renewed) res = await send(method, path, opts, renewed.accessToken);
    }
    if (!res.ok) throw await toError(res);
    if (opts.raw) return res as T;
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    request,
    get: (path, query) => request('GET', path, query ? { query } : {}),
    async login(input) {
      const res = await send('POST', '/v1/auth/login', { body: input }, null);
      if (!res.ok) throw await toError(res);
      const next = (await res.json()) as Session;
      setSession(next);
      return next;
    },
    refresh,
    current: () => session,
    async logout() {
      try {
        await send('POST', '/v1/auth/logout', {}, null);
      } finally {
        setSession(null);
      }
    },
    onSession(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
